import { getIdToken } from './auth';

const wsUrl = (import.meta.env.VITE_WS_URL || '').replace(/\/$/, '');

const HEARTBEAT_MS = 4 * 60 * 1000; // API Gateway closes idle sockets after 10 minutes.
const MAX_LIFETIME_MS = 100 * 60 * 1000; // API Gateway ends any connection at 2 hours; renew first with a fresh token.
const MAX_BACKOFF_MS = 60 * 1000;

// Opens the Command Center push socket. The socket only signals that new events were stored; the caller
// then reads them through the normal authenticated API, so no guest data travels over the socket.
// Statuses: 'off' (not configured), 'connecting', 'push', 'polling' (socket down; interval polling continues).
export function connectRealtime({ onSignal, onStatus }) {
  if (!wsUrl || typeof WebSocket === 'undefined') {
    onStatus('off');
    return () => {};
  }

  let socket = null;
  let stopped = false;
  let attempt = 0;
  let retryTimer = null;
  let heartbeatTimer = null;
  let lifetimeTimer = null;

  function schedule() {
    const delay = Math.min(MAX_BACKOFF_MS, 2000 * 2 ** attempt) + Math.random() * 1000;
    attempt += 1;
    retryTimer = setTimeout(open, delay);
  }

  async function open() {
    clearTimeout(retryTimer);
    if (stopped) return;
    let token;
    try { token = await getIdToken(); } catch { onStatus('polling'); schedule(); return; }
    if (stopped) return;

    const ws = new WebSocket(`${wsUrl}?token=${encodeURIComponent(token)}`);
    socket = ws;
    ws.onopen = () => {
      attempt = 0;
      onStatus('push');
      heartbeatTimer = setInterval(() => { try { ws.send('{"action":"ping"}'); } catch { /* the close handler reconnects */ } }, HEARTBEAT_MS);
      lifetimeTimer = setTimeout(() => ws.close(), MAX_LIFETIME_MS);
      onSignal(); // catch up on anything that arrived while the socket was down
    };
    ws.onmessage = (message) => {
      try {
        const payload = JSON.parse(message.data);
        if (payload.type === 'events') onSignal(payload);
      } catch { /* ignore malformed frames */ }
    };
    ws.onclose = () => {
      clearInterval(heartbeatTimer);
      clearTimeout(lifetimeTimer);
      if (socket === ws) socket = null;
      if (!stopped) { onStatus('polling'); schedule(); }
    };
    ws.onerror = () => { try { ws.close(); } catch { /* already closed */ } };
  }

  onStatus('connecting');
  open();

  return () => {
    stopped = true;
    clearTimeout(retryTimer);
    clearInterval(heartbeatTimer);
    clearTimeout(lifetimeTimer);
    if (socket) { try { socket.close(); } catch { /* already closed */ } }
  };
}
